import { api } from "@/app/services/api.service";

export const DOCUMENT_SYNC_PATH = "record-search/document-sync/by/";
export const DOCUMENT_SYNC_RESEND_COPIES_PATH = "record-search/certification/resend-copies";
export const documentSyncByIdPath = (syncId: string) => `record-search/document-sync/by-id/${syncId}`;
export const documentSyncClosePath = (syncId: string) => `record-search/document-sync/items/${syncId}/close`;
export const documentSyncManualDownloadPath = (itemId: string) =>
  `record-search/document-sync/manual-download/${itemId}`;

export const RECORD_SEARCH_STATUSES = ["PENDING", "SENT", "FAILED", "CANCELLED"] as const;

export interface RecordSearchDocumentSync {
  id: string | null;
  certificationId: string | null;
  trackingNumber: string | null;
  brn: string | null;
  brnSource: string | null;
  companyName: string | null;
  status: string | null;
  updatedAt: string | null;
  raw: Record<string, unknown>;
}

export interface RecordSearchSyncItem {
  id: string | null;
  syncId: string | null;
  documentName: string | null;
  documentEdmsId: string | null;
  status: string | null;
  updatedAt: string | null;
  raw: Record<string, unknown>;
}

export interface RecordSearchDocumentSyncDetail extends RecordSearchDocumentSync {
  items: RecordSearchSyncItem[];
}

export interface RecordSearchFilters {
  page: number;
  perPage: number;
  status: string;
  brn?: string;
  companyName?: string;
}

export interface RecordSearchPage {
  content: RecordSearchDocumentSync[];
  totalElements: number;
  totalPages: number;
}

interface ApiEnvelope<T> {
  error?: string | null;
  message?: string | null;
  data?: T;
  status?: number;
}

interface PaginatedSyncResponse {
  content?: unknown[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  perPage?: number;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return err.response?.data?.message
    || err.response?.data?.error
    || err.message
    || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

function unwrapEnvelope<T>(root: unknown): T {
  if (root != null && typeof root === "object" && "data" in root) {
    const envelope = root as ApiEnvelope<T>;
    if (envelope.error) {
      throw new Error(envelope.message || envelope.error);
    }
    if (envelope.data == null) {
      throw new Error(envelope.message || "Empty API response");
    }
    return envelope.data;
  }
  return root as T;
}

function normalizeItem(item: unknown): RecordSearchDocumentSync | null {
  if (item == null || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;

  return {
    id: readString(record, ["id"]),
    certificationId: readString(record, ["certificationId", "certification_id"]),
    trackingNumber: readString(record, ["trackingNumber", "tracking_number"]),
    brn: readString(record, ["brn", "businessRegistrationNumber", "registrationNumber"]),
    brnSource: readString(record, ["brnSource", "brn_source"]),
    companyName: readString(record, ["companyName", "company_name", "name"]),
    status: readString(record, ["status"]),
    updatedAt: readString(record, ["updatedAt", "updated_at"]),
    raw: record,
  };
}

function normalizeSyncItem(item: unknown): RecordSearchSyncItem | null {
  if (item == null || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;

  return {
    id: readString(record, ["id", "itemId", "item_id"]),
    syncId: readString(record, ["syncId", "sync_id"]),
    documentName: readString(record, ["documentName", "document_name", "name"]),
    documentEdmsId: readString(record, ["documentEdmsId", "document_edms_id", "edmsId"]),
    status: readString(record, ["status", "itemStatus"]),
    updatedAt: readString(record, ["updatedAt", "updated_at"]),
    raw: record,
  };
}

function extractSyncItems(record: Record<string, unknown>): RecordSearchSyncItem[] {
  if (!Array.isArray(record.items)) return [];
  return record.items
    .map((item) => normalizeSyncItem(item))
    .filter((item): item is RecordSearchSyncItem => item != null);
}

function extractPageRecords(root: unknown): RecordSearchPage {
  const pageData = unwrapEnvelope<PaginatedSyncResponse | Record<string, unknown>>(root);

  if (pageData != null && typeof pageData === "object" && Array.isArray((pageData as PaginatedSyncResponse).content)) {
    const paginated = pageData as PaginatedSyncResponse;
    const content = (paginated.content ?? [])
      .map((item) => normalizeItem(item))
      .filter((item): item is RecordSearchDocumentSync => item != null);

    return {
      content,
      totalElements: Number(paginated.totalElements ?? content.length),
      totalPages: Number(paginated.totalPages ?? 0),
    };
  }

  if (Array.isArray(pageData)) {
    const content = pageData
      .map((item) => normalizeItem(item))
      .filter((item): item is RecordSearchDocumentSync => item != null);

    return {
      content,
      totalElements: content.length,
      totalPages: content.length > 0 ? 1 : 0,
    };
  }

  const single = normalizeItem(pageData);
  if (single) {
    return { content: [single], totalElements: 1, totalPages: 1 };
  }

  return { content: [], totalElements: 0, totalPages: 0 };
}

export async function fetchDocumentSyncRecords(filters: RecordSearchFilters): Promise<RecordSearchPage> {
  const params: Record<string, string | number> = {
    page: filters.page,
    perPage: filters.perPage,
    status: filters.status,
  };
  if (filters.brn?.trim()) params.brn = filters.brn.trim();
  if (filters.companyName?.trim()) params.companyName = filters.companyName.trim();

  const res = await api().get(DOCUMENT_SYNC_PATH, { params });
  return extractPageRecords(res.data);
}

export function resolveSyncId(row: RecordSearchDocumentSync): string | null {
  return row.id || readString(row.raw, ["id"]);
}

export function resolveTrackingNumber(row: RecordSearchDocumentSync): string | null {
  return row.trackingNumber || readString(row.raw, ["trackingNumber", "tracking_number"]);
}

export function resolveItemId(item: RecordSearchSyncItem): string | null {
  return item.id || readString(item.raw, ["id", "itemId", "item_id"]);
}

function parseFilenameFromDisposition(header: string | undefined, fallback: string) {
  if (!header) return fallback;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (match?.[1]) return match[1].replace(/['"]/g, "");
  return fallback;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function resendDocumentSyncCopies(trackingNumber: string): Promise<void> {
  await api().post(DOCUMENT_SYNC_RESEND_COPIES_PATH, { trackingNumber });
}

export async function resendSearchReportRequest(trackingNumber: string, recreate: boolean) {
  const path = `record-search/search-report-request/resend/${encodeURIComponent(trackingNumber)}?recreate=${recreate}`;
  return api().post(path);
}

export async function closeDocumentSyncItem(syncId: string): Promise<void> {
  await api().post(documentSyncClosePath(syncId));
}

export async function downloadDocumentSyncItem(itemId: string, suggestedFilename?: string | null): Promise<void> {
  const response = await api().get(documentSyncManualDownloadPath(itemId), { responseType: "blob" });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const fallback = suggestedFilename || `document-${itemId}`;
  const filename = parseFilenameFromDisposition(response.headers["content-disposition"], fallback);
  triggerBlobDownload(blob, filename);
}

export async function fetchDocumentSyncById(syncId: string): Promise<RecordSearchDocumentSyncDetail> {
  const res = await api().get(documentSyncByIdPath(syncId));
  const data = unwrapEnvelope<Record<string, unknown>>(res.data);
  const normalized = normalizeItem(data);

  if (!normalized) {
    throw new Error("Invalid sync record response");
  }

  return {
    ...normalized,
    items: extractSyncItems(data),
  };
}
